import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, errMsg, fmtDate } from "../lib/api";
import ReportView from "../components/report/ReportView";

export default function PrintReport() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const audience = params.get("audience") === "internal" ? "internal" : "prospect";
  const [data, setData] = useState(null);
  const [services, setServices] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/locations/${id}/report?audience=${audience}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
    api.get("/services").then((r) => setServices(Object.fromEntries(r.data.map((s) => [s.key, s.name])))).catch(() => {});
  }, [id, audience]);

  if (error) return <div className="p-10 text-red-700">{error}</div>;
  if (!data) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  if (!data.report) return <div className="p-10">No report yet.</div>;
  const loc = data.location;
  return (
    <div className="mx-auto max-w-5xl bg-white px-6 py-8" data-testid={`print-report-${audience}`}>
      <div className="no-print mb-6 flex items-center justify-between rounded-md bg-slate-900 p-3 text-sm text-white">
        <span>{audience === "prospect" ? "Prospect-facing version — internal notes, qualification and sales judgments excluded." : "INTERNAL version — contains sales notes. Do not share."}</span>
        <Button size="sm" onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700" data-testid="print-btn"><Printer className="mr-1 h-3.5 w-3.5" />Print / Save as PDF</Button>
      </div>
      <header className="mb-6 border-b-4 border-orange-600 pb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-orange-600">360 Digital Marketing · Public presence review</div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">{data.report.restaurant.name}</h1>
        <div className="text-sm text-slate-600">{data.report.restaurant.address} · Prepared {fmtDate(data.report.created_at)}</div>
        <p className="mt-2 text-xs text-slate-500">Based on automated review of publicly available sources on the date shown. Coverage: {data.report.coverage.level}. This is not a comprehensive audit; items marked "Not found in checked sources" or "Unable to assess" have not been established either way.</p>
      </header>
      <ReportView data={data} audience={audience} services={services} />
      {audience === "internal" && loc.notes?.length > 0 && (
        <section className="print-card mt-6 rounded-lg border p-5" data-testid="print-internal-notes">
          <h2 className="mb-2 text-lg font-bold">Internal sales notes</h2>
          <div className="mb-2 text-sm">Qualification: {loc.qualification_status} · Next action: {loc.next_action || "—"} · Follow-up: {loc.follow_up_date || "—"}</div>
          {loc.notes.map((n) => <p key={n.id} className="border-l-2 pl-2 text-sm">{n.text} <span className="text-xs text-slate-500">— {n.author}</span></p>)}
        </section>
      )}
    </div>
  );
}
